import { create } from 'zustand';

// --------------------------------------------------------------------------------
// Store Implementation
// --------------------------------------------------------------------------------

/**
 * Drawer store for managing sidebar drawer states
 * 
 * Handles which drawer is currently open from sidebar navigation
 * 
 * Components should use this store directly with Zustand selectors:
 * ```typescript
 * const activeDrawer = useDrawerStore(state => state.activeDrawer);
 * const openDrawer = useDrawerStore(state => state.openDrawer);
 * ```
 */
const useDrawerStore = create<DrawerStore>((set) => ({
  activeDrawer: null,
  isCollapsed: false,
  
  setActiveDrawer: (drawer) => set({ activeDrawer: drawer, isCollapsed: false }),
  
  openDrawer: (drawer) => set({ activeDrawer: drawer, isCollapsed: false }),
  
  closeDrawer: () => set({ activeDrawer: null, isCollapsed: false }),
  
  toggleDrawer: (drawer) => set((state) => ({
    activeDrawer: state.activeDrawer === drawer ? null : drawer,
    isCollapsed: state.activeDrawer === drawer ? false : false,
  })),
  
  toggleCollapse: () => set((state) => ({
    isCollapsed: !state.isCollapsed,
  })),
  
  setCollapsed: (collapsed) => set({ isCollapsed: collapsed }),
}));

export default useDrawerStore;

// --------------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------------

/**
 * Drawer types that can be opened from the sidebar
 */
export type DrawerType = 'groups' | 'search' | 'notifications' | 'tasks' | 'ai-chat' | null;

/**
 * Drawer store for managing sidebar drawer states
 * 
 * Handles which drawer is currently open from sidebar navigation
 */
type DrawerStore = {
  /**
   * Currently open drawer type
   */
  activeDrawer: DrawerType;
  /**
   * Whether the active drawer is collapsed
   */
  isCollapsed: boolean;
  /**
   * Set the active drawer
   */
  setActiveDrawer: (drawer: DrawerType) => void;
  /**
   * Open a specific drawer (closes others)
   */
  openDrawer: (drawer: DrawerType) => void;
  /**
   * Close the active drawer
   */
  closeDrawer: () => void;
  /**
   * Toggle a drawer (opens if closed, closes if open)
   */
  toggleDrawer: (drawer: DrawerType) => void;
  /**
   * Toggle collapsed state of the active drawer
   */
  toggleCollapse: () => void;
  /**
   * Set collapsed state
   */
  setCollapsed: (collapsed: boolean) => void;
};

